import api from '@/lib/api';
import i18n from '@/lib/i18n';
import {
  ChatRole,
  ToolCallRecord,
  MessageFeedback,
  PageContext,
  ExecuteActionRequest,
  ExecuteActionResponse,
  normalizeChatLocale,
} from '@hr-outsourcing/shared';
import { streamingFetch, parseSSEStream, StreamingApiError } from '@/lib/streamingApi';
import { EntityId } from '@/types/common';
export type FeedbackRating = 'positive' | 'negative';
export interface ChatConversation {
  id: EntityId;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}
export interface ChatMessage {
  id: EntityId;
  role: ChatRole;
  content: string;
  toolCalls: ToolCallRecord[] | null;
  createdAt: string;
}
export interface ConversationDetail {
  conversation: ChatConversation;
  messages: ChatMessage[];
}
const getChatLocale = () => normalizeChatLocale(i18n.resolvedLanguage ?? i18n.language);

const chatService = {
  async createConversation(title?: string): Promise<ChatConversation> {
    const response = await api.post('/chatbot/conversations', { title });
    return response.data.data.conversation;
  },
  async getConversations(): Promise<ChatConversation[]> {
    const response = await api.get('/chatbot/conversations');
    return response.data.data.conversations;
  },
  async getConversation(id: EntityId): Promise<ConversationDetail> {
    const response = await api.get(`/chatbot/conversations/${id}`);
    return response.data.data;
  },
  async deleteConversation(id: EntityId): Promise<void> {
    await api.delete(`/chatbot/conversations/${id}`);
  },
  async sendMessage(conversationId: EntityId, content: string, pageContext?: PageContext): Promise<ChatMessage> {
    const response = await api.post(`/chatbot/conversations/${conversationId}/messages`, {
      content,
      pageContext,
      locale: getChatLocale(),
    });
    return response.data.data.message;
  },
  async getSuggestions(): Promise<string[]> {
    const response = await api.get('/chatbot/suggestions', { params: { locale: getChatLocale() } });
    return response.data.data.suggestions;
  },
  async streamMessage(
    conversationId: EntityId,
    content: string,
    pageContext?: PageContext
  ): Promise<{
    stream: AsyncGenerator<{
      event: string;
      data: unknown;
    }>;
    abort: () => void;
  }> {
    const { reader, abort } = await streamingFetch(`/chatbot/conversations/${conversationId}/messages/stream`, {
      method: 'POST',
      body: { content, pageContext, locale: getChatLocale() },
    });
    return {
      stream: parseSSEStream(reader),
      abort,
    };
  },
  async submitFeedback(messageId: EntityId, rating: FeedbackRating, feedbackText?: string): Promise<void> {
    try {
      await api.post(`/chatbot/messages/${messageId}/feedback`, {
        rating,
        feedbackText,
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  },
  async executeAction(request: ExecuteActionRequest): Promise<ExecuteActionResponse> {
    const response = await api.post('/chatbot/actions/execute', request);
    return response.data.data;
  },
};
export { StreamingApiError };
export default chatService;
